import React, { createRef } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import {
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Popover,
  TextField,
  Tooltip,
} from '@mui/material';

import { ParagonColors, ParaLogo } from '@paragon/client-tools';
import { AnalysisHistoryRecord } from '@paragon/contract-library';

import { RootState } from '../../../store';
import {
  changeAnalysisHistoryRecordName,
  revertToComponentDesignFromAnalysisSet,
  setActiveAnalysisSet,
} from '../../../store/actions/appActions';
import { isViewingOtherOrganizationsComponent } from '../../../store/selectors/viewOnlyModeSelectors';

const activeFill = ParagonColors.paragold;
const inactiveFill = '#9e9e9e';

interface OwnProps {
  active: boolean;
  analysisHistoryRecord: AnalysisHistoryRecord;
  isWorkingSet: boolean;
  key: string;
  commitColor: string;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = OwnProps & StateProps & DispatchProps;

interface State {
  name: string;
  namePopoverOpen: boolean;
}

export function formatTimestampForAnalysisHistoryRecord(timestamp: string) {
  return new Date(timestamp).toLocaleString();
}

class AnalysisHistoryRecordSubPanel extends React.Component<Props, State> {
  editNameRef = createRef<HTMLButtonElement>();

  readonly state: State = {
    name: this.props.analysisHistoryRecord.name,
    namePopoverOpen: false,
  };

  onChangeNameClick = async () => {
    if (this.props.viewingOtherOrganizationsComponent) return;

    await this.selectAndLoadAsActiveAnalysisSet();
    this.setState({ namePopoverOpen: true });
  };

  onRestoreClick: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    if (this.props.viewingOtherOrganizationsComponent) return;

    event.stopPropagation();

    const analysisSet = await this.selectAndLoadAsActiveAnalysisSet();
    if (!analysisSet) {
      console.error('Attempted to restore non-existent analysis set.');
      return;
    }

    this.props.revertToComponentDesignFromAnalysisSet(analysisSet);
  };

  selectAndLoadAsActiveAnalysisSet = () => {
    if (this.props.locked || this.props.viewingOtherOrganizationsComponent) {
      return Promise.resolve(undefined);
    }

    return this.props.setActiveAnalysisSet(this.props.analysisHistoryRecord.analysisSetGuid);
  };

  onNameChange: React.ReactEventHandler<HTMLInputElement> = (event) => {
    this.setState({ name: event.currentTarget.value });
  };

  onNameInputKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      this.onChangeCommitNameSubmit();
    }
  };

  onChangeCommitNameSubmit = () => {
    this.props.changeAnalysisHistoryRecordName(this.props.analysisHistoryRecord, this.state.name);
    this.setState({ namePopoverOpen: false });
  };

  render() {
    const name = (
      <span>
        <span style={{ color: this.props.commitColor, whiteSpace: 'normal' }}>
          {this.props.analysisHistoryRecord.name}
        </span>
        <Popover
          id="mouse-over-popover"
          sx={{ '& .MuiPopover-paper': { p: 1 } }}
          open={this.state.namePopoverOpen}
          anchorEl={this.editNameRef.current}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          onClose={this.onChangeCommitNameSubmit}
          disableRestoreFocus
        >
          <TextField
            variant="standard"
            id="standard-name"
            value={this.state.name}
            onKeyPress={this.onNameInputKeyPress}
            onChange={(evn: any) => this.onNameChange(evn)}
            margin="normal"
          />
        </Popover>
      </span>
    );

    return (
      <ListItemButton onClick={() => this.selectAndLoadAsActiveAnalysisSet()} selected={this.props.active}>
        <Tooltip disableInteractive title={name} placement="right-start" leaveDelay={600}>
          <ListItemIcon>
            <ParaLogo width={25} height={25} viewBox="0 0 32 28" fill={this.props.active ? activeFill : inactiveFill} />
          </ListItemIcon>
        </Tooltip>
        <ListItemText
          primary={name}
          secondary={formatTimestampForAnalysisHistoryRecord(this.props.analysisHistoryRecord.timestamp)}
          sx={{ pr: 6 }}
        />

        {!this.props.viewingOtherOrganizationsComponent && !this.props.isInSharedComponentSetContext && (
          <ListItemSecondaryAction>
            <Tooltip disableInteractive title="Rename" placement="right">
              <IconButton onClick={this.onChangeNameClick} aria-label="Edit">
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip
              disableInteractive
              placement="right"
              title={this.props.isWorkingSet ? 'Active analysis' : 'Restore component'}
            >
              <span>
                <IconButton
                  onClick={this.onRestoreClick}
                  aria-label="Restore"
                  disabled={this.props.isWorkingSet}
                  ref={this.editNameRef}
                >
                  <RestoreIcon />
                </IconButton>
              </span>
            </Tooltip>
          </ListItemSecondaryAction>
        )}
      </ListItemButton>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  locked: state.analysisSets.loadingAnalysisSet,
  viewingOtherOrganizationsComponent: isViewingOtherOrganizationsComponent(state),
  isInSharedComponentSetContext: state.application.openContext?.type === 'shared-component-set',
});

const actionCreators = {
  setActiveAnalysisSet,
  changeAnalysisHistoryRecordName,
  revertToComponentDesignFromAnalysisSet,
};

export default connect(mapStateToProps, actionCreators)(AnalysisHistoryRecordSubPanel);
